/**
 * Travel Desk — the administration of the module: the on/off switch, desk settings, versioned
 * policies with their entitlement grid and per-diem rule, and the city-tier list (ADR 0030, plan
 * §5.3 / §5.6). The traveller's side is `travel-request.contract.ts`, the desk's `travel-desk.contract.ts`,
 * the reports `travel-report.contract.ts`; all under `/travel`. Declared on its own (invariant 8)
 * before any table, route or screen consumes it.
 *
 * WHO. `travel.policy:manage` (expense manager, HR admin, org owner) writes the switch, policies,
 * entitlements, per-diem rules and city tiers. `travel.desk:manage` (travel desk manager, org owner)
 * writes the desk settings — the fallback team and the desk's SLA clock — because they run the desk,
 * not the policy. Reads take either of those or `travel:read`: HR and finance look at the policy
 * without being able to change it. Every signed-in user may ask whether the module is on
 * (`getModuleStatus`, `null` metadata) — the navigation needs it before anyone holds a travel grant.
 *
 * THE MODULE SWITCH (D33, EC-61). `TravelSettings.enabled` defaults to OFF. While it is off every
 * `/travel` route answers 404 `module_disabled`, the navigation is hidden, and the jobs skip the
 * organisation; data is kept, and switching it on again resumes where things were. THIS router is the
 * exception: `getModuleStatus`, `getSettings` and `updateSettings` still answer while the module is
 * off, or it could never be switched on. The other admin routes are 404 like the rest.
 *
 * POLICIES ARE EFFECTIVE-DATED VERSIONS (invariant 5, D12). A `TravelPolicy` row has `validFrom` and
 * `validTo` (inclusive, `null` = open) and belongs to one legal entity or, with `legalEntityId: null`,
 * to the whole organisation; the entity's own policy beats the organisation's on any day it is in
 * force (`policyInForce`). A trip reads the version in force on ITS START DATE, so a new version never
 * changes a trip already planned under the old one. Nothing that has history is overwritten:
 *   - `createPolicy` starts a new version. When an open-ended version of the same scope is in force at
 *     `validFrom`, the server ends it the day before, in the same transaction; two versions of one
 *     scope never cover the same day (409 `policy_overlap`).
 *   - `updatePolicy` and both `put…` grid routes edit a version that has NOT started (`phase:
 *     'scheduled'`). Once it is in force or past, only `validTo` may change (to end it early); a
 *     correction is a new version (409 `policy_in_force`). `deletePolicy` removes only a scheduled one.
 *   - `phase` is derived from the dates in the legal entity's timezone (the organisation's default
 *     for an organisation-wide policy), never stored.
 * A policy's `currency` is its legal entity's currency; an organisation-wide policy applies only to
 * entities that use that currency (EC-6). The route refuses a mismatch with 422.
 *
 * THE GRID. `TravelEntitlement` rows say what a grade band may do per mode and city tier: which
 * classes are allowed, a cap (hotel: per night; per diem and lodging in lieu: per day), whether the
 * traveller may book it themself (D1, D29), and which classes are hard-blocked (a hard block
 * refuses submission outright; there is no override, D24 was dropped). The grid is written as a
 * WHOLE (`putEntitlements` replaces every row, like the helpdesk SLA matrix) so a screen that edits a grid sends a grid; the schema refuses classes
 * that do not belong to the row's mode, an allowed class that is also blocked, a reversed grade band,
 * and two rows of the same mode and tier whose bands overlap. A tier-specific row and an all-tiers
 * row of the same mode MAY overlap: the exact tier wins (`travel/policy.ts`). Grade bands are ranks
 * from the grade catalogue (ADR 0022); a null bound is open.
 *
 * CITY TIERS (D26). `city` is stored in its normalised spelling (`normalizeCityKey`: NFKC, trimmed,
 * single spaces, lower case), so "  New  Delhi" and "new delhi" are one row; the route normalises what
 * it is sent. India needs no setup: while an organisation has no rows for a country, the built-in
 * HRA X / Y / Z list (`INDIA_CITY_TIERS`, with common aliases) applies to India; any rows the
 * organisation stores for a country replace that list, and another country starts empty. A city
 * with no row is `tier_3`, so a row is only ever needed to make a city dearer — or, by storing
 * `tier_3` explicitly, to override a listed one.
 *
 * MONEY is a decimal string (invariant 2) in the policy's currency. Percentages are decimal strings
 * of 0–100 with at most two places (`percentSchema`). Configuration writes take no Idempotency-Key
 * (they are not financial mutations, invariant 10) but every mutable row carries `version` and a
 * stale write is a 409 `stale_version`. Machine codes (`module_disabled`, `stale_version`,
 * `policy_overlap`, `policy_in_force`) arrive in `details.code`; the full list is `TRAVEL_ERROR_CODES`.
 */

import { Decimal } from 'decimal.js';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { TRAVEL_CLASS_LEG_MODE } from '../enums.js';
import {
  countrySchema,
  currencySchema,
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  paginatedSchema,
  percentSchema,
  uuidSchema,
} from '../schemas/common.js';
import {
  travelAmountSchema,
  travelBookingModeSchema,
  travelCityTierSchema,
  travelClassSchema,
  travelClockTimeSchema,
  travelEntitlementModeSchema,
  travelEntitlementRowSchema,
  travelPerDiemRuleSnapshotSchema,
  travelVersionSchema,
} from '../travel/schemas.js';
import { normalizeCityKey } from '../travel/overlap.js';
import { routeMeta } from './meta.js';

const c = initContract();

const policyParams = z.object({ policyId: uuidSchema });
const cityTierParams = z.object({ cityTierId: uuidSchema });

const deleteResultSchema = z.object({ success: z.boolean() });

// ---------------------------------------------------------------------------
// Settings — the module switch and the desk's clock
// ---------------------------------------------------------------------------

/**
 * `business` measures the desk's SLA in the organisation's working hours (`businessDayStart` to
 * `businessDayEnd` on the location's working days), `calendar` in plain hours — the helpdesk's two
 * clocks (ADR 0028 §6). Stored due instants say which one they were measured in.
 */
export const TRAVEL_DESK_SLA_CLOCKS = ['business', 'calendar'] as const;
export const travelDeskSlaClockSchema = z.enum(TRAVEL_DESK_SLA_CLOCKS);
export type TravelDeskSlaClock = z.infer<typeof travelDeskSlaClockSchema>;

/** What a fresh organisation starts with. `enabled` is off until the pilot switches it on (D33). */
export const TRAVEL_SETTINGS_DEFAULTS = {
  enabled: false,
  defaultBookingMode: 'desk',
  /** Drafts and unanswered invitations older than this are removed by the weekly cleanup (EC-42). */
  draftRetentionDays: 60,
  deskSlaClock: 'business',
  businessDayStart: '09:00',
  businessDayEnd: '18:00',
  /** D19: the desk's deadline is this many working hours after approval, or sooner near departure. */
  deskSlaWorkingHours: 8,
} as const;

/** One row per organisation. */
export const travelSettingsSchema = z.object({
  /** D33. Off: nav hidden, routes 404 `module_disabled`, jobs skip the organisation. */
  enabled: z.boolean(),
  /** What the New Trip form starts on; a grade that may not self-book is always `desk`. */
  defaultBookingMode: travelBookingModeSchema,
  draftRetentionDays: z.number().int().min(7).max(365),
  /** The desk team that takes a trip no team's coverage reaches (D30, EC-21). Null: none. */
  fallbackTeamId: uuidSchema.nullable(),
  fallbackTeamName: z.string().nullable(),
  deskSlaClock: travelDeskSlaClockSchema,
  businessDayStart: travelClockTimeSchema,
  businessDayEnd: travelClockTimeSchema,
  /** D19. Working hours (or plain hours on the `calendar` clock) from approval to the desk's deadline. */
  deskSlaWorkingHours: z.number().int().min(1).max(168),
  version: travelVersionSchema,
  updatedAt: instantSchema,
});
export type TravelSettings = z.infer<typeof travelSettingsSchema>;

/** The one thing every signed-in user may ask (`getModuleStatus`): is Travel Desk on for my organisation. */
export const travelModuleStatusSchema = z.object({
  enabled: z.boolean(),
  defaultBookingMode: travelBookingModeSchema,
});
export type TravelModuleStatus = z.infer<typeof travelModuleStatusSchema>;

/** The policy owner's half of the settings: the switch and the form's defaults. */
export const updateTravelSettingsBodySchema = z.object({
  version: travelVersionSchema,
  enabled: z.boolean().optional(),
  defaultBookingMode: travelBookingModeSchema.optional(),
  draftRetentionDays: z.number().int().min(7).max(365).optional(),
});
export type UpdateTravelSettingsBody = z.infer<typeof updateTravelSettingsBodySchema>;

/** The desk manager's half: who catches what nobody covers, and how the desk's clock runs. */
export const updateTravelDeskSettingsBodySchema = z
  .object({
    version: travelVersionSchema,
    /** Must be an active desk team; `null` clears it. */
    fallbackTeamId: uuidSchema.nullable().optional(),
    deskSlaClock: travelDeskSlaClockSchema.optional(),
    businessDayStart: travelClockTimeSchema.optional(),
    businessDayEnd: travelClockTimeSchema.optional(),
    deskSlaWorkingHours: z.number().int().min(1).max(168).optional(),
  })
  .refine(
    (body) =>
      !body.businessDayStart || !body.businessDayEnd || body.businessDayEnd > body.businessDayStart,
    { message: 'The working day must end after it starts', path: ['businessDayEnd'] },
  );
export type UpdateTravelDeskSettingsBody = z.infer<typeof updateTravelDeskSettingsBodySchema>;

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

/**
 * Who bears a cancellation charge by default (D27). `by_reason`: a business reason is the company's,
 * a personal one the employee's (recovered through the D4 ladder); the other two ignore the reason.
 * The desk may override any single charge with a note; the override is audited and shown on the trip.
 */
export const TRAVEL_CANCELLATION_CHARGE_RULES = ['by_reason', 'company', 'employee'] as const;
export const travelCancellationChargeRuleSchema = z.enum(TRAVEL_CANCELLATION_CHARGE_RULES);
export type TravelCancellationChargeRule = z.infer<typeof travelCancellationChargeRuleSchema>;

/**
 * How a fare is paid when the traveller adds personal days to a business trip (EC-5).
 * `capped_to_business_fare`: the company pays no more than the fare the business days alone would have
 * cost and the employee the difference; `company_pays_all`: the company pays the whole booked fare.
 * Personal days themselves never earn on-duty, per diem or comp-off, whichever rule is set.
 */
export const TRAVEL_PERSONAL_DAY_FARE_RULES = [
  'capped_to_business_fare',
  'company_pays_all',
] as const;
export const travelPersonalDayFareRuleSchema = z.enum(TRAVEL_PERSONAL_DAY_FARE_RULES);
export type TravelPersonalDayFareRule = z.infer<typeof travelPersonalDayFareRuleSchema>;

/** Where a policy version stands against the calendar, derived in its legal entity's timezone. */
export const TRAVEL_POLICY_PHASES = ['scheduled', 'in_force', 'ended'] as const;
export const travelPolicyPhaseSchema = z.enum(TRAVEL_POLICY_PHASES);
export type TravelPolicyPhase = z.infer<typeof travelPolicyPhaseSchema>;

/**
 * What `createPolicy` uses for a field the body leaves out. The plan fixes only some of these
 * (`postFactoMaxDays` 15 D14, `supplementaryWindowDays` 30 D13, `bookingTolerancePct` 10 D11,
 * `maxPayrollRecoveryPctOfGross` 50 D22, `compOffForOffDayTravel` on D21, by-reason charges D27); the
 * rest are conservative starting points an admin is expected to look at.
 */
export const TRAVEL_POLICY_DEFAULTS = {
  leadTimeDays: 0,
  postFactoMaxDays: 15,
  maxAdvancePct: null,
  maxAdvanceAmount: null,
  blockAdvanceWhenOverdue: false,
  settleWithinDays: 15,
  supplementaryWindowDays: 30,
  bookingTolerancePct: '10',
  cancellationChargeRule: 'by_reason',
  personalDayFareRule: 'capped_to_business_fare',
  compOffForOffDayTravel: true,
  maxPayrollRecoveryPctOfGross: '50',
  recoveryConsentText: null,
} as const;

/** The rule fields of a policy, all required — what a policy IS. Create adds defaults, update makes them optional. */
const policyRuleShape = {
  /** The legal entity's currency (EC-6); the route refuses a mismatch with 422. */
  currency: currencySchema,
  /** Minimum whole days between filing and departure; a shorter notice is a soft violation. */
  leadTimeDays: z.number().int().min(0).max(365),
  /** How long after a trip a post-facto request is still accepted (D14). */
  postFactoMaxDays: z.number().int().min(0).max(90),
  /** Percent of the trip's estimate an advance may be. Null: no percentage cap. */
  maxAdvancePct: percentSchema.nullable(),
  /** Absolute ceiling on an advance, in `currency`. Null: none. Both caps apply when both are set. */
  maxAdvanceAmount: travelAmountSchema.nullable(),
  /** Refuse a new advance while the traveller has another one overdue (EC-37). */
  blockAdvanceWhenOverdue: z.boolean(),
  /** Days after the trip's end the main settlement is due; drives the advance's due date. */
  settleWithinDays: z.number().int().min(1).max(180),
  /** Days after the main settlement in which late receipts can still be added (D13). */
  supplementaryWindowDays: z.number().int().min(0).max(180),
  /** A booking above the approved estimate by more than this percent becomes an amendment (D11). */
  bookingTolerancePct: percentSchema,
  cancellationChargeRule: travelCancellationChargeRuleSchema,
  personalDayFareRule: travelPersonalDayFareRuleSchema,
  /** D21: travel on a weekly off or holiday earns a pending comp-off credit. */
  compOffForOffDayTravel: z.boolean(),
  /** D22: the most of monthly gross one payroll run may recover from an overdue advance. */
  maxPayrollRecoveryPctOfGross: percentSchema,
  /** What the traveller ticks when asking for an advance (D22). Legal confirms the wording. */
  recoveryConsentText: z.string().trim().min(1).max(2000).nullable(),
};

const endNotBeforeStart = (body: { validFrom?: string; validTo?: string | null }): boolean =>
  !body.validFrom || !body.validTo || body.validTo >= body.validFrom;
const VALID_TO_BEFORE_FROM = {
  message: 'The end date is before the start date',
  path: ['validTo'],
};

/** One policy version as listed. */
export const travelPolicySchema = z.object({
  id: uuidSchema,
  /** Null: the whole organisation. */
  legalEntityId: uuidSchema.nullable(),
  legalEntityName: z.string().nullable(),
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable(),
  phase: travelPolicyPhaseSchema,
  /** True only while `phase` is `scheduled`: the rest of the policy may still be edited. */
  isEditable: z.boolean(),
  ...policyRuleShape,
  entitlementCount: z.number().int().nonnegative(),
  hasPerDiemRule: z.boolean(),
  version: travelVersionSchema,
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type TravelPolicy = z.infer<typeof travelPolicySchema>;

/** One entitlement row as stored: the wire shape the form evaluates, plus its identity. */
export const travelEntitlementSchema = travelEntitlementRowSchema.extend({ id: uuidSchema });
export type TravelEntitlement = z.infer<typeof travelEntitlementSchema>;

/** One policy version with its grid and per-diem rule — what the policy editor loads. */
export const travelPolicyDetailSchema = travelPolicySchema.extend({
  entitlements: z.array(travelEntitlementSchema),
  perDiemRule: travelPerDiemRuleSnapshotSchema.nullable(),
});
export type TravelPolicyDetail = z.infer<typeof travelPolicyDetailSchema>;

export const listTravelPoliciesQuerySchema = z.object({
  /** One entity's own versions and the organisation-wide ones that can apply to it. */
  legalEntityId: uuidSchema.optional(),
  phase: travelPolicyPhaseSchema.optional(),
});

/**
 * Start a new version. `copyEntitlementsFromPolicyId` copies that version's grid and per-diem rule
 * into the new one (the rule fields come from THIS body — a screen pre-fills them from the source);
 * omit it for an empty grid. Fields left out take `TRAVEL_POLICY_DEFAULTS`.
 */
export const createTravelPolicyBodySchema = z
  .object({
    /** Null (the default): organisation-wide. */
    legalEntityId: uuidSchema.nullable().default(null),
    validFrom: dateOnlySchema,
    validTo: dateOnlySchema.nullable().default(null),
    currency: policyRuleShape.currency,
    leadTimeDays: policyRuleShape.leadTimeDays.default(TRAVEL_POLICY_DEFAULTS.leadTimeDays),
    postFactoMaxDays: policyRuleShape.postFactoMaxDays.default(
      TRAVEL_POLICY_DEFAULTS.postFactoMaxDays,
    ),
    maxAdvancePct: policyRuleShape.maxAdvancePct.default(TRAVEL_POLICY_DEFAULTS.maxAdvancePct),
    maxAdvanceAmount: policyRuleShape.maxAdvanceAmount.default(
      TRAVEL_POLICY_DEFAULTS.maxAdvanceAmount,
    ),
    blockAdvanceWhenOverdue: policyRuleShape.blockAdvanceWhenOverdue.default(
      TRAVEL_POLICY_DEFAULTS.blockAdvanceWhenOverdue,
    ),
    settleWithinDays: policyRuleShape.settleWithinDays.default(
      TRAVEL_POLICY_DEFAULTS.settleWithinDays,
    ),
    supplementaryWindowDays: policyRuleShape.supplementaryWindowDays.default(
      TRAVEL_POLICY_DEFAULTS.supplementaryWindowDays,
    ),
    bookingTolerancePct: policyRuleShape.bookingTolerancePct.default(
      TRAVEL_POLICY_DEFAULTS.bookingTolerancePct,
    ),
    cancellationChargeRule: policyRuleShape.cancellationChargeRule.default(
      TRAVEL_POLICY_DEFAULTS.cancellationChargeRule,
    ),
    personalDayFareRule: policyRuleShape.personalDayFareRule.default(
      TRAVEL_POLICY_DEFAULTS.personalDayFareRule,
    ),
    compOffForOffDayTravel: policyRuleShape.compOffForOffDayTravel.default(
      TRAVEL_POLICY_DEFAULTS.compOffForOffDayTravel,
    ),
    maxPayrollRecoveryPctOfGross: policyRuleShape.maxPayrollRecoveryPctOfGross.default(
      TRAVEL_POLICY_DEFAULTS.maxPayrollRecoveryPctOfGross,
    ),
    recoveryConsentText: policyRuleShape.recoveryConsentText.default(
      TRAVEL_POLICY_DEFAULTS.recoveryConsentText,
    ),
    copyEntitlementsFromPolicyId: uuidSchema.optional(),
  })
  .refine(endNotBeforeStart, VALID_TO_BEFORE_FROM);
export type CreateTravelPolicyBody = z.infer<typeof createTravelPolicyBodySchema>;

/**
 * Edit a policy version. Every field is optional; `version` is the token from the last read. Scope
 * (`legalEntityId`) never changes — start a new version instead. On a version that is in force or
 * past, only `validTo` may be sent (409 `policy_in_force` otherwise); see the header.
 */
export const updateTravelPolicyBodySchema = z
  .object({
    version: travelVersionSchema,
    validFrom: dateOnlySchema.optional(),
    validTo: dateOnlySchema.nullable().optional(),
    currency: policyRuleShape.currency.optional(),
    leadTimeDays: policyRuleShape.leadTimeDays.optional(),
    postFactoMaxDays: policyRuleShape.postFactoMaxDays.optional(),
    maxAdvancePct: policyRuleShape.maxAdvancePct.optional(),
    maxAdvanceAmount: policyRuleShape.maxAdvanceAmount.optional(),
    blockAdvanceWhenOverdue: policyRuleShape.blockAdvanceWhenOverdue.optional(),
    settleWithinDays: policyRuleShape.settleWithinDays.optional(),
    supplementaryWindowDays: policyRuleShape.supplementaryWindowDays.optional(),
    bookingTolerancePct: policyRuleShape.bookingTolerancePct.optional(),
    cancellationChargeRule: policyRuleShape.cancellationChargeRule.optional(),
    personalDayFareRule: policyRuleShape.personalDayFareRule.optional(),
    compOffForOffDayTravel: policyRuleShape.compOffForOffDayTravel.optional(),
    maxPayrollRecoveryPctOfGross: policyRuleShape.maxPayrollRecoveryPctOfGross.optional(),
    recoveryConsentText: policyRuleShape.recoveryConsentText.optional(),
  })
  .refine(endNotBeforeStart, VALID_TO_BEFORE_FROM);
export type UpdateTravelPolicyBody = z.infer<typeof updateTravelPolicyBodySchema>;

// ---------------------------------------------------------------------------
// Entitlement grid
// ---------------------------------------------------------------------------

const gradeRankSchema = z.number().int().min(0).max(10_000);

/**
 * One row of the grid as written. Amounts are decimal strings in the policy's currency; a null bound
 * or cap is open. Bookable classes belong to the row's mode (`TRAVEL_CLASS_LEG_MODE`), so hotel, per
 * diem, lodging in lieu and mileage rows carry none.
 */
export const travelEntitlementInputSchema = z
  .object({
    mode: travelEntitlementModeSchema,
    gradeRankMin: gradeRankSchema.nullable().default(null),
    gradeRankMax: gradeRankSchema.nullable().default(null),
    /** Empty: no class restriction for this mode. */
    allowedClasses: z.array(travelClassSchema).max(17).default([]),
    /** Null: the row applies to every city tier (a tier-specific row of the same mode wins). */
    cityTier: travelCityTierSchema.nullable().default(null),
    /** Hotel: nightly cap. Per diem and lodging in lieu: the daily amount. Null: no cap. */
    capAmount: travelAmountSchema.nullable().default(null),
    /** D1, D29: the traveller may book this themself; expenses are then claimed in a `pre_trip` round. */
    selfBookingAllowed: z.boolean().default(false),
    /** Nobody in this band may book these without a `travel:approve` override (D24). */
    hardBlockedClasses: z.array(travelClassSchema).max(17).default([]),
  })
  .superRefine((row, ctx) => {
    if (
      row.gradeRankMin !== null &&
      row.gradeRankMax !== null &&
      row.gradeRankMin > row.gradeRankMax
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['gradeRankMax'],
        message: 'The top of the grade band is below its bottom',
      });
    }
    for (const field of ['allowedClasses', 'hardBlockedClasses'] as const) {
      row[field].forEach((cls, index) => {
        if (TRAVEL_CLASS_LEG_MODE[cls] !== row.mode) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field, index],
            message: 'This class does not belong to the row’s mode',
          });
        }
      });
    }
    if (row.allowedClasses.some((cls) => row.hardBlockedClasses.includes(cls))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hardBlockedClasses'],
        message: 'A class cannot be both allowed and blocked',
      });
    }
  });
export type TravelEntitlementInput = z.infer<typeof travelEntitlementInputSchema>;

const bandsOverlap = (
  a: { gradeRankMin: number | null; gradeRankMax: number | null },
  b: { gradeRankMin: number | null; gradeRankMax: number | null },
): boolean =>
  (a.gradeRankMin ?? -Infinity) <= (b.gradeRankMax ?? Infinity) &&
  (b.gradeRankMin ?? -Infinity) <= (a.gradeRankMax ?? Infinity);

/**
 * Replace the whole grid of a scheduled policy. Two rows of the same mode AND tier (all-tiers counts
 * as one tier) may not cover a common grade, or the traveller's entitlement would be ambiguous.
 */
export const putTravelEntitlementsBodySchema = z.object({
  /** The POLICY's token: the grid is part of that version. */
  version: travelVersionSchema,
  entitlements: z
    .array(travelEntitlementInputSchema)
    .max(500)
    .superRefine((rows, ctx) => {
      rows.forEach((row, j) => {
        for (let i = 0; i < j; i += 1) {
          const other = rows[i]!;
          if (
            other.mode === row.mode &&
            other.cityTier === row.cityTier &&
            bandsOverlap(other, row)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [j, 'gradeRankMin'],
              message: `Overlaps row ${i + 1}: same mode and city tier, and the grade bands share a grade`,
            });
            return;
          }
        }
      });
    }),
});
export type PutTravelEntitlementsBody = z.infer<typeof putTravelEntitlementsBodySchema>;

// ---------------------------------------------------------------------------
// Per-diem rule
// ---------------------------------------------------------------------------

/** Hours as a decimal string with at most two places, 0 < h <= 24. */
const hoursSchema = z
  .string()
  .regex(/^\d{1,2}(\.\d{1,2})?$/, 'Use hours, e.g. 4 or 4.5')
  .refine(
    (value) => new Decimal(value).gt(0) && new Decimal(value).lte(24),
    'Between 0 and 24 hours',
  );

/**
 * The per-diem rule (`computePerDiem`): a day shorter than `halfDayBelowHours` pays half; the first
 * and last day pay a percentage of the day's rate; each meal the company provides deducts its
 * percentage. The rates themselves are the grid's `per_diem` rows, by city tier.
 */
export const travelPerDiemRuleInputSchema = z
  .object({
    /** Null: no half days. */
    halfDayBelowHours: hoursSchema.nullable(),
    firstDayPct: percentSchema,
    lastDayPct: percentSchema,
    breakfastPct: percentSchema,
    lunchPct: percentSchema,
    dinnerPct: percentSchema,
  })
  .refine(
    (rule) => new Decimal(rule.breakfastPct).plus(rule.lunchPct).plus(rule.dinnerPct).lte(100),
    { message: 'The meal deductions add up to more than 100%', path: ['dinnerPct'] },
  );
export type TravelPerDiemRuleInput = z.infer<typeof travelPerDiemRuleInputSchema>;

/** Set the rule of a scheduled policy; `rule: null` removes it (no per diem is then estimated). */
export const putTravelPerDiemRuleBodySchema = z.object({
  /** The POLICY's token. */
  version: travelVersionSchema,
  rule: travelPerDiemRuleInputSchema.nullable(),
});
export type PutTravelPerDiemRuleBody = z.infer<typeof putTravelPerDiemRuleBodySchema>;

// ---------------------------------------------------------------------------
// City tiers
// ---------------------------------------------------------------------------

/** A city as the admin typed it; the route stores it as `normalizeCityKey` spells it. */
const cityInputSchema = z.string().trim().min(1).max(120);

export const travelCityTierRowSchema = z.object({
  id: uuidSchema,
  country: countrySchema,
  /** Normalised: lower case, single spaces. Screens capitalise for display. */
  city: z.string(),
  tier: travelCityTierSchema,
});
export type TravelCityTierRow = z.infer<typeof travelCityTierRowSchema>;

export const listTravelCityTiersQuerySchema = z.object({
  country: countrySchema.optional(),
  tier: travelCityTierSchema.optional(),
  /** Substring of the city name. */
  q: z.string().trim().min(1).max(120).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** Set one city's tier: creates the row or changes it. */
export const putTravelCityTierBodySchema = z.object({
  country: countrySchema,
  city: cityInputSchema,
  tier: travelCityTierSchema,
});

/** Set many at once (a pasted list, or a country's classification). Cities repeated in one call are refused. */
export const bulkPutTravelCityTiersBodySchema = z
  .object({
    country: countrySchema,
    rows: z
      .array(z.object({ city: cityInputSchema, tier: travelCityTierSchema }))
      .min(1)
      .max(500),
  })
  .refine(
    (body) => new Set(body.rows.map((row) => normalizeCityKey(row.city))).size === body.rows.length,
    { message: 'A city appears more than once', path: ['rows'] },
  );

export const bulkPutTravelCityTiersResultSchema = z.object({
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  /** Already had that tier. */
  unchanged: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------------------
// Contract — literal paths before `:param` paths (contract-route-order)
// ---------------------------------------------------------------------------

/** Anyone who administers or audits the policy may read it. */
const readPolicyMeta = routeMeta(['travel.policy:manage', 'travel:read']);
const writePolicyMeta = routeMeta('travel.policy:manage');

export const travelPolicyContract = c.router(
  {
    // ---- Module switch and settings ---------------------------------------------------------

    /** Exempt from the module switch: the navigation asks this before anything else. */
    getModuleStatus: {
      method: 'GET',
      path: '/status',
      responses: {
        200: travelModuleStatusSchema,
        401: errorBodySchema,
      },
      summary: 'Is Travel Desk switched on for this organisation',
      metadata: routeMeta(null),
    },

    getSettings: {
      method: 'GET',
      path: '/settings',
      responses: {
        200: travelSettingsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Travel Desk settings: the module switch, form defaults, and the desk clock',
      metadata: routeMeta(['travel.policy:manage', 'travel.desk:manage']),
    },

    /** Exempt from the module switch, or it could never be switched on. */
    updateSettings: {
      method: 'PATCH',
      path: '/settings',
      body: updateTravelSettingsBodySchema,
      responses: {
        200: travelSettingsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Stale `version`. */
        409: errorBodySchema,
      },
      summary: 'Switch Travel Desk on or off, and set the New Trip form defaults',
      metadata: writePolicyMeta,
    },

    updateDeskSettings: {
      method: 'PATCH',
      path: '/settings/desk',
      body: updateTravelDeskSettingsBodySchema,
      responses: {
        200: travelSettingsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** The fallback team is not an active desk team. */
        422: errorBodySchema,
      },
      summary: "Set the desk's fallback team and SLA clock",
      metadata: routeMeta('travel.desk:manage'),
    },

    // ---- Policies (effective-dated versions) ------------------------------------------------

    listPolicies: {
      method: 'GET',
      path: '/policies',
      query: listTravelPoliciesQuerySchema,
      responses: {
        200: z.array(travelPolicySchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'List the travel policy versions, newest start date first',
      metadata: readPolicyMeta,
    },

    createPolicy: {
      method: 'POST',
      path: '/policies',
      body: createTravelPolicyBodySchema,
      responses: {
        201: travelPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Another version of the same scope already covers `validFrom`, and cannot be ended before it. */
        409: errorBodySchema,
        /** Currency does not match the legal entity's, or the entity / copy source does not exist. */
        422: errorBodySchema,
      },
      summary:
        'Start a new policy version; the open-ended version it replaces is ended the day before',
      metadata: writePolicyMeta,
    },

    getPolicy: {
      method: 'GET',
      path: '/policies/:policyId',
      pathParams: policyParams,
      responses: {
        200: travelPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One policy version with its entitlement grid and per-diem rule',
      metadata: readPolicyMeta,
    },

    updatePolicy: {
      method: 'PATCH',
      path: '/policies/:policyId',
      pathParams: policyParams,
      body: updateTravelPolicyBodySchema,
      responses: {
        200: travelPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Stale `version`, `policy_in_force` (only `validTo` may change), or `policy_overlap`. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Edit a scheduled policy version; an in-force one can only be ended early',
      metadata: writePolicyMeta,
    },

    deletePolicy: {
      method: 'DELETE',
      path: '/policies/:policyId',
      pathParams: policyParams,
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** In force or past: it is history now (`policy_in_force`). */
        409: errorBodySchema,
      },
      summary: 'Delete a policy version that has not started',
      metadata: writePolicyMeta,
    },

    putEntitlements: {
      method: 'PUT',
      path: '/policies/:policyId/entitlements',
      pathParams: policyParams,
      body: putTravelEntitlementsBodySchema,
      responses: {
        200: travelPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Stale `version`, or `policy_in_force`. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Replace a scheduled policy's whole entitlement grid (grade band × mode × city tier)",
      metadata: writePolicyMeta,
    },

    putPerDiemRule: {
      method: 'PUT',
      path: '/policies/:policyId/per-diem-rule',
      pathParams: policyParams,
      body: putTravelPerDiemRuleBodySchema,
      responses: {
        200: travelPolicyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Set or remove a scheduled policy's per-diem rule",
      metadata: writePolicyMeta,
    },

    // ---- City tiers -------------------------------------------------------------------------

    listCityTiers: {
      method: 'GET',
      path: '/city-tiers',
      query: listTravelCityTiersQuerySchema,
      responses: {
        200: paginatedSchema(travelCityTierRowSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'List cities with an explicit tier; any city not listed is tier 3',
      metadata: readPolicyMeta,
    },

    putCityTier: {
      method: 'PUT',
      path: '/city-tiers',
      body: putTravelCityTierBodySchema,
      responses: {
        200: travelCityTierRowSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Set a city's tier, creating the row or changing it",
      metadata: writePolicyMeta,
    },

    bulkPutCityTiers: {
      method: 'POST',
      path: '/city-tiers/bulk',
      body: bulkPutTravelCityTiersBodySchema,
      responses: {
        200: bulkPutTravelCityTiersResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Set the tier of many cities of one country in a single call',
      metadata: writePolicyMeta,
    },

    deleteCityTier: {
      method: 'DELETE',
      path: '/city-tiers/:cityTierId',
      pathParams: cityTierParams,
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Remove a city’s row; it falls back to tier 3',
      metadata: writePolicyMeta,
    },
  },
  { pathPrefix: '/travel/admin' },
);
